import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  SubuserPermissionsService,
  RolePermissionPageItem,
  ClientRoleItem,
  PermissionActionDef,
  PERMISSION_ACTIONS,
} from '@services/subuser-permissions.service';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';

export interface TreePageItem extends RolePermissionPageItem {
  children?: TreePageItem[];
  level?: number;
  [key: string]: any;
}

export interface SubUserRow {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  created_at?: string;
  role_id: number;
  isUpdating?: boolean;
  /** Controlled open state for the role picker — see onSubUserRoleChange(). */
  roleDropdownOpen?: boolean;
}

@Component({
  selector: 'app-roles-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    NzModalModule,
    NzSwitchModule,
    NzSelectModule,
    NzTableModule,
    NzToolTipModule,
  ],
  templateUrl: './roles-management.component.html',
  styleUrls: ['./roles-management.component.scss'],
})
export class RolesManagementComponent implements OnInit {
  private permissionsService = inject(SubuserPermissionsService);
  private sharedService = inject(SharedService);
  private secureStorage = inject(SecureStorageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private modal = inject(NzModalService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  // General State
  isLoadingRoles = false;
  isLoadingPermissions = false;
  isLoadingUsers = false;
  isSavingPermissions = false;
  activeTab: 'permissions' | 'users' = 'permissions';

  // Filters & Search
  pageSearchTerm = '';
  userSearchTerm = '';

  // Data
  rolesList: ClientRoleItem[] = [];
  selectedRoleId: number = 0;
  selectedRole: ClientRoleItem | null = null;

  // Permissions state.
  // treePages holds the SAME object references as rawPages (only re-parented), so a
  // bulk action on rawPages is reflected by the switches immediately.
  rawPages: TreePageItem[] = [];
  treePages: TreePageItem[] = [];
  originalPermissionsHash = '';
  allowedCount = 0;
  totalPages = 0;

  /** View / Create / Edit / Delete column definitions — drives the table and the payload. */
  readonly permissionActions: PermissionActionDef[] = PERMISSION_ACTIONS;

  /**
   * True when the table is rendered from the local fallback page list because the API
   * returned no pages. Those page ids are placeholders, not real `client_pages` rows,
   * so saving them fails with "One or more page ids are not client pages" — Save is
   * blocked and a banner explains why instead of letting the user hit that error.
   */
  isUsingFallbackPages = false;

  // Sub-Users state
  subUsersList: SubUserRow[] = [];
  /** Sub-user deep-linked from User Management, highlighted in the users tab. */
  focusSubUserId = 0;
  editRoleName!: string;
  editRoleDescription!: string;
  inlineEditNameError!: string;
  isEditRoleModalVisible!: boolean;
  isUpdatingRole!: boolean;
  isDeleteRoleModalVisible!: boolean;
  isDeletingRole!: boolean;

  private getParentId(): number {
    const userDetails = this.secureStorage.getItem<any>('userDetails');
    const id = Number(userDetails?.id || userDetails?.user_id || 0);
    return id > 0 ? id : this.permissionsService.getEffectiveParentId();
  }

  /** Sub-users carrying the role currently open, for the per-role badge. */
  get usersOnActiveRole(): number {
    if (!this.selectedRoleId) return 0;
    return this.subUsersList.filter(
      (u) => this.permissionsService.getSubUserAssignedRole(u, this.rolesList) === Number(this.selectedRoleId)
    ).length;
  }

  async ngOnInit(): Promise<void> {

    const tab = this.route.snapshot.queryParams['tab'];
    this.activeTab = tab === 'users' ? 'users' : 'permissions';

    const queryRoleId = Number(this.route.snapshot.queryParams['roleId']);

    // User Management links here with ?userId= to assign one person a role.
    // That parameter used to be ignored, dropping the user on a random role's
    // permission grid instead.
    this.focusSubUserId = Number(this.route.snapshot.queryParams['userId']) || 0;
    if (this.focusSubUserId > 0 && !tab) {
      this.activeTab = 'users';
    }

    await this.loadInitialData(queryRoleId);
  }

  goBack(): void {
    if (this.router.url.includes('policy-management')) {
      this.router.navigate(['/home/policy-management/roles']);
    } else {
      this.router.navigate(['/home/roles']);
    }
  }

  async loadInitialData(preferredRoleId?: number): Promise<void> {
    this.isLoadingRoles = true;
    this.sharedService.showLoader();
    try {
      const roles = await firstValueFrom(
        this.permissionsService.getClientRoles().pipe(
          timeout(5000),
          catchError(() => of([]))
        )
      );

      this.rolesList = roles || [];

      if (this.rolesList.length > 0) {
        let active = this.rolesList[0];
        if (preferredRoleId && this.rolesList.some((r) => r.id === preferredRoleId)) {
          active = this.rolesList.find((r) => r.id === preferredRoleId)!;
        }
        this.selectRole(active);
      } else {
        // Fallback default role if backend has no roles yet
        const defaultRole: ClientRoleItem = {
          id: 1,
          policy_name: 'Manager',
          description: 'Default manager role with configurable page access.',
        };
        this.rolesList = [defaultRole];
        this.selectRole(defaultRole);
      }
    } catch (err) {
      console.warn('[RolesManagement] Failed to load client roles:', err);
    } finally {
      this.isLoadingRoles = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }

    // Also load sub-users for assignment tab
    await this.loadSubUsers();
  }

  selectRole(role: ClientRoleItem): void {
    if (this.hasUnsavedChanges) {
      this.modal.confirm({
        nzTitle: 'Unsaved Permission Changes',
        nzContent: `You have unsaved changes in "${this.selectedRole?.policy_name}". Do you want to discard them and switch to "${role.policy_name}"?`,
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-warning',
        nzOkText: 'Discard & Switch',
        nzCancelText: 'Keep Editing',
        nzOkDanger: true,
        nzOnOk: () => {
          this.doSelectRole(role);
        },
      });
    } else {
      this.doSelectRole(role);
    }
  }

  private async doSelectRole(role: ClientRoleItem): Promise<void> {
    this.selectedRole = role;
    this.selectedRoleId = Number(role.id);

    // Update query param
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        roleId: role.id,
        tab: this.activeTab === 'users' ? 'users' : null,
      },
      queryParamsHandling: 'merge',
    });

    await this.loadRolePermissions(this.selectedRoleId);
  }

  /**
   * The page rows come from GET /client_pages and the toggles from
   * GET /client_role_permissions, merged in the service. Anything created on the
   * Pages screen therefore appears here on the next load, with its real row id.
   */
  async loadRolePermissions(roleId: number): Promise<void> {
    this.isLoadingPermissions = true;
    this.sharedService.showLoader();
    try {
      const res = await firstValueFrom(
        this.permissionsService.getRolePagePermissions(roleId).pipe(
          timeout(12000),
          catchError((err) => {
            console.warn(`[RolesManagement] getRolePagePermissions failed for role ${roleId}:`, err);
            return of({
              role: { id: roleId, policy_name: this.selectedRole?.policy_name || `Role #${roleId}` },
              allowed_count: 0,
              total_pages: 0,
              pages: [],
              usedFallback: true,
            });
          })
        )
      );

      console.log('[RolesManagement] Loaded role permissions over /client_pages:', res);

      // Saving is only safe when the ids came from /client_pages itself.
      this.isUsingFallbackPages = res.usedFallback === true;

      this.rawPages = (res.pages || []).map((p) => ({
        page_id: Number(p.page_id),
        page_name: p.page_name || '',
        page_key: p.page_key || '',
        url: p.url || '',
        parent_id: p.parent_id !== undefined && p.parent_id !== null ? Number(p.parent_id) : null,
        can_view: p.can_view === true,
        can_create: p.can_create === true,
        can_edit: p.can_edit === true,
        can_delete: p.can_delete === true,
      }));

      this.allowedCount = this.rawPages.filter((p) => p.can_view).length;
      this.totalPages = this.rawPages.length || (typeof res.total_pages === 'number' ? res.total_pages : 0);

      this.originalPermissionsHash = this.getPermissionsHash();
      this.buildTree();
    } catch (err) {
      console.warn(`[RolesManagement] Error loading permissions for role ${roleId}:`, err);
    } finally {
      this.isLoadingPermissions = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  /**
   * Build hierarchical tree using parent_id:
   * Parent pages have parent_id == null.
   * Child pages have parent_id == parent.page_id.
   */
  buildTree(): void {
    const parentMap = new Map<number, TreePageItem>();
    const roots: TreePageItem[] = [];
    const childrenList: TreePageItem[] = [];

    // First pass: identify root parents and children.
    // NOTE: the tree reuses the rawPages objects (no spread/copy) — otherwise the
    // switches would be bound to clones and bulk actions on rawPages would silently
    // update the counters while leaving every toggle visually off.
    this.rawPages.forEach((treeItem) => {
      treeItem.children = [];
      treeItem.level = 0;
      if (treeItem.parent_id === null || treeItem.parent_id === undefined || treeItem.parent_id === 0) {
        parentMap.set(treeItem.page_id, treeItem);
        roots.push(treeItem);
      } else {
        childrenList.push(treeItem);
      }
    });

    // Second pass: attach children to parents
    childrenList.forEach((child) => {
      if (child.parent_id && parentMap.has(child.parent_id)) {
        const parent = parentMap.get(child.parent_id)!;
        child.level = 1;
        parent.children!.push(child);
      } else {
        // If parent not found, show at root level
        roots.push(child);
      }
    });

    this.treePages = roots;
  }

  /**
   * Toggle one CRUD flag on one page. nz-switch has already written the new value
   * via ngModel, so this only enforces the View-gates-everything rule:
   * - turning View off clears Create/Edit/Delete
   * - turning any of Create/Edit/Delete on implies View
   */
  onToggleAction(page: RolePermissionPageItem, action: PermissionActionDef): void {
    if (action.key === 'view') {
      if (!page.can_view) {
        page.can_create = false;
        page.can_edit = false;
        page.can_delete = false;
      }
    } else if (page[action.field]) {
      page.can_view = true;
    }

    this.recalcCounts();
  }

  /** True when the switch should be locked — CRUD columns need View first. */
  isActionDisabled(page: RolePermissionPageItem, action: PermissionActionDef): boolean {
    return action.key !== 'view' && !page.can_view;
  }

  /** Grant/revoke a whole column (e.g. Delete for every page) from the table header. */
  toggleColumn(action: PermissionActionDef, grant: boolean): void {
    this.rawPages.forEach((p) => {
      if (action.key === 'view') {
        p.can_view = grant;
        if (!grant) {
          p.can_create = false;
          p.can_edit = false;
          p.can_delete = false;
        }
      } else {
        p[action.field] = grant;
        if (grant) p.can_view = true;
      }
    });

    this.recalcCounts();
  }

  /** How many pages currently have this action granted — shown in the column header. */
  countForAction(action: PermissionActionDef): number {
    return this.rawPages.filter((p) => p[action.field]).length;
  }

  /** Grant/revoke every CRUD flag on a single row. */
  toggleRow(page: RolePermissionPageItem, grant: boolean): void {
    page.can_view = grant;
    page.can_create = grant;
    page.can_edit = grant;
    page.can_delete = grant;
    this.recalcCounts();
  }

  /** True when the row has all four flags granted. */
  isRowFullyGranted(page: RolePermissionPageItem): boolean {
    return page.can_view && page.can_create && page.can_edit && page.can_delete;
  }

  private recalcCounts(): void {
    this.allowedCount = this.rawPages.filter((p) => p.can_view).length;
  }

  // Bulk actions deliberately show no dialog — the result is visible in the table
  // straight away, and createNotification() opens a blocking modal.
  grantAll(): void {
    this.rawPages.forEach((p) => {
      p.can_view = true;
      p.can_create = true;
      p.can_edit = true;
      p.can_delete = true;
    });
    this.recalcCounts();
  }

  revokeAll(): void {
    this.rawPages.forEach((p) => {
      p.can_view = false;
      p.can_create = false;
      p.can_edit = false;
      p.can_delete = false;
    });
    this.recalcCounts();
  }

  get hasUnsavedChanges(): boolean {
    return this.getPermissionsHash() !== this.originalPermissionsHash;
  }

  private getPermissionsHash(): string {
    return this.rawPages
      .map(
        (p) =>
          `${p.page_id}:${p.can_view ? 1 : 0}${p.can_create ? 1 : 0}${p.can_edit ? 1 : 0}${p.can_delete ? 1 : 0}`
      )
      .sort()
      .join('|');
  }

  /**
   * 4. Save the toggles:
   * POST /client_role_permissions_bulk
   * - Send the WHOLE set back, not just the ones that changed.
   * - Show a warning before saving: changing a role affects EVERY sub-user who has it.
   */
  promptSavePermissions(): void {
    if (this.isUsingFallbackPages) {
      this.sharedService.createNotification(
        'error',
        'Page list could not be loaded from the server, so these permissions cannot be saved. Please try again once client pages are available.'
      );
      return;
    }

    this.modal.confirm({
      nzTitle: 'Save Role Permissions?',
      nzContent: `Warning: Changing the permissions for "${this.selectedRole?.policy_name}" affects EVERY sub-user currently assigned to this role. Do you want to proceed?`,
      nzCentered: true,
      nzClassName: 'contest-styled-modal contest-modal-warning',
      nzOkText: 'Yes, Save Changes',
      nzCancelText: 'Cancel',
      nzOkDanger: false,
      nzOnOk: () => this.executeSavePermissions(),
    });
  }

  async executeSavePermissions(): Promise<void> {
    if (!this.selectedRoleId) return;

    this.isSavingPermissions = true;
    try {
      const payloadPermissions = this.rawPages.map((p) => ({
        page_id: Number(p.page_id),
        can_view: Boolean(p.can_view),
        can_create: Boolean(p.can_create),
        can_edit: Boolean(p.can_edit),
        can_delete: Boolean(p.can_delete),
      }));

      await firstValueFrom(
        this.permissionsService
          .updateClientRolePermissionsBulk(this.selectedRoleId, payloadPermissions)
          .pipe(timeout(6000))
      );

      this.originalPermissionsHash = this.getPermissionsHash();
      // other screens show this role's granted-page count, keep them in step
      this.permissionsService.refreshPolicySummary(this.getParentId());
      this.sharedService.createNotification(
        'success',
        `Role "${this.selectedRole?.policy_name}" permissions updated successfully!`
      );
    } catch (err: any) {
      console.error('[RolesManagement] Error saving permissions bulk:', err);
      const msg = err?.error?.message || err?.message || 'Failed to save role permissions.';
      this.sharedService.createNotification('error', msg);
    } finally {
      this.isSavingPermissions = false;
      this.cdr.detectChanges();
    }
  }

  revertChanges(): void {
    if (this.selectedRoleId) {
      this.loadRolePermissions(this.selectedRoleId);
      this.sharedService.createNotification('info', 'Unsaved changes reverted.');
    }
  }

  /**
   * 5. Assign a role to a sub-user:
   * GET /get_sub_users
   * POST /assign_subuser_role { "sub_user_id": 941, "role_id": 5 }
   * - To remove a role, send "role_id": 0.
   * - Each sub-user has AT MOST ONE role. Render as single-select dropdown.
   */
  async loadSubUsers(): Promise<void> {
    this.isLoadingUsers = true;
    this.sharedService.showLoader();
    try {
      const parentId = this.getParentId();
      const res = await firstValueFrom(
        this.permissionsService.getSubUsersByParent(parentId, 1, 100).pipe(
          timeout(5000),
          catchError(() => of({ items: [] }))
        )
      );

      const items = res?.items || res?.data || (Array.isArray(res) ? res : []);
      this.subUsersList = items.map((u: any, idx: number) => ({
        id: Number(u.id ?? u.sub_user_id ?? idx + 1),
        name: u.name || u.username || u.full_name || `Sub-user #${u.id}`,
        email: u.email || 'user@company.com',
        avatar: u.profile_image || u.avatar,
        created_at: u.created_at,
        role_id: this.permissionsService.getSubUserAssignedRole(u, this.rolesList),
        isUpdating: false,
      }));
    } catch (err) {
      console.warn('[RolesManagement] Error loading sub-users:', err);
    } finally {
      this.isLoadingUsers = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  async onSubUserRoleChange(user: SubUserRow, newRoleId: any): Promise<void> {
    const targetRoleId = Number(newRoleId);
    const previousRoleId = Number(user.role_id) || 0;

    // Close the popup on this very click rather than leaving it to settle later.
    user.roleDropdownOpen = false;

    // The select is no longer disabled while the assign is in flight (disabling it in
    // the same tick as the option click fought with its own close), so guard here.
    if (user.isUpdating || targetRoleId === previousRoleId) {
      this.cdr.detectChanges();
      return;
    }

    user.isUpdating = true;
    // Show the picked role straight away; the catch below puts it back on failure.
    user.role_id = targetRoleId;
    this.cdr.detectChanges();

    try {
      const res = await firstValueFrom(
        this.permissionsService.assignSubUserRole(user.id, targetRoleId).pipe(timeout(5000))
      );

      // the service caches on success; keep the shared counts in step
      this.permissionsService.refreshPolicySummary(this.getParentId());

      const roleObj = this.rolesList.find((r) => Number(r.id) === targetRoleId);
      const roleName = roleObj ? roleObj.policy_name : 'No Role (Access Denied)';

      const successMsg =
        res?.message ||
        (targetRoleId > 0
          ? `Assigned "${user.name}" to role "${roleName}".`
          : `Removed role from "${user.name}".`);

      this.sharedService.createNotification('success', successMsg);
    } catch (err: any) {
      console.warn('[RolesManagement] assignSubUserRole error:', err);
      // Report the failure and put the dropdown back. This used to claim success
      // and cache the assignment locally, so the Roles screen counted a member
      // the backend never stored, and another browser showed none.
      user.role_id = previousRoleId;
      const message =
        err?.error?.message || err?.message || `Could not assign a role to "${user.name}". Please try again.`;
      this.sharedService.createNotification('error', message);
    } finally {
      user.isUpdating = false;
      this.cdr.detectChanges();
    }
  }

  get filteredSubUsers(): SubUserRow[] {
    const term = (this.userSearchTerm || '').toLowerCase().trim();
    if (!term) return this.subUsersList;
    return this.subUsersList.filter(
      (u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term)
    );
  }

  private matchesSearch(page: TreePageItem, term: string): boolean {
    return (
      page.page_name.toLowerCase().includes(term) ||
      page.page_key.toLowerCase().includes(term) ||
      page.url.toLowerCase().includes(term)
    );
  }

  /**
   * Parent rows to render. Returns the original objects — never copies — so the
   * switches stay bound to the same records the bulk actions mutate.
   */
  get filteredTreePages(): TreePageItem[] {
    const term = (this.pageSearchTerm || '').toLowerCase().trim();
    if (!term) return this.treePages;

    return this.treePages.filter(
      (parent) =>
        this.matchesSearch(parent, term) ||
        (parent.children || []).some((c) => this.matchesSearch(c, term))
    );
  }

  /** Child rows to render under a parent, narrowed by the search term. */
  visibleChildren(parent: TreePageItem): TreePageItem[] {
    const children = parent.children || [];
    const term = (this.pageSearchTerm || '').toLowerCase().trim();
    if (!term || this.matchesSearch(parent, term)) return children;

    const matched = children.filter((c) => this.matchesSearch(c, term));
    return matched.length > 0 ? matched : children;
  }

  collapsedParents = new Set<number>();

  toggleCollapse(id: number | undefined, event?: MouseEvent): void {
    event?.stopPropagation();
    const key = Number(id);
    if (this.collapsedParents.has(key)) {
      this.collapsedParents.delete(key);
    } else {
      this.collapsedParents.add(key);
    }
  }

  isCollapsed(id: number | undefined): boolean {
    return this.collapsedParents.has(Number(id));
  }

  get hasAnyChildren(): boolean {
    return this.treePages.some((p) => p.children && p.children.length > 0);
  }

  get allCollapsed(): boolean {
    const parentsWithKids = this.treePages.filter((p) => p.children && p.children.length > 0);
    return (
      parentsWithKids.length > 0 &&
      parentsWithKids.every((p) => this.collapsedParents.has(Number(p.page_id)))
    );
  }

  toggleAll(): void {
    if (this.allCollapsed) {
      this.collapsedParents.clear();
      return;
    }
    this.collapsedParents = new Set(
      this.treePages
        .filter((p) => p.children && p.children.length > 0)
        .map((p) => Number(p.page_id))
    );
  }

  get coveragePercent(): number {
    if (this.totalPages === 0) return 0;
    return Math.round((this.allowedCount / this.totalPages) * 100);
  }

  get assignedUsersCountForActiveRole(): number {
    if (!this.selectedRoleId) return 0;
    return this.subUsersList.filter((u) => Number(u.role_id) === Number(this.selectedRoleId)).length;
  }

  // Edit Active Role
  openEditRoleModal(): void {
    if (!this.selectedRole) return;
    this.editRoleName = this.selectedRole.policy_name || '';
    this.editRoleDescription = this.selectedRole.description || '';
    this.inlineEditNameError = '';
    this.isEditRoleModalVisible = true;
  }

  closeEditRoleModal(): void {
    this.isEditRoleModalVisible = false;
    this.inlineEditNameError = '';
  }

  onEditRoleNameInput(): void {
    if (this.inlineEditNameError) {
      this.inlineEditNameError = '';
    }
  }

  async handleEditRoleSubmit(): Promise<void> {
    if (!this.selectedRole) return;
    const name = this.editRoleName.trim();
    const desc = this.editRoleDescription.trim();

    if (!name) {
      this.inlineEditNameError = 'Role name is required.';
      return;
    }

    this.isUpdatingRole = true;
    this.inlineEditNameError = '';

    try {
      await firstValueFrom(
        this.permissionsService.updateClientRole(this.selectedRole.id, name, desc).pipe(
          timeout(6000)
        )
      );

      this.selectedRole.policy_name = name;
      this.selectedRole.description = desc;

      const found = this.rolesList.find((r) => r.id === this.selectedRole?.id);
      if (found) {
        found.policy_name = name;
        found.description = desc;
      }

      this.sharedService.createNotification('success', `Role "${name}" updated successfully!`);
      this.closeEditRoleModal();
    } catch (err: any) {
      console.warn('[RolesManagement] updateClientRole error:', err);
      const errMsg =
        err?.error?.message ||
        err?.error?.error ||
        err?.message ||
        'Failed to update role.';
      this.inlineEditNameError = errMsg;
    } finally {
      this.isUpdatingRole = false;
      this.cdr.detectChanges();
    }
  }

  // Delete Active Role
  promptDeleteActiveRole(): void {
    if (!this.selectedRole) return;
    this.isDeleteRoleModalVisible = true;
  }

  closeDeleteRoleModal(): void {
    this.isDeleteRoleModalVisible = false;
  }

  async executeDeleteActiveRole(): Promise<void> {
    if (!this.selectedRole) return;

    const roleToDelete = this.selectedRole;
    this.isDeletingRole = true;

    try {
      await firstValueFrom(
        this.permissionsService.deleteClientRole(roleToDelete.id).pipe(
          timeout(6000)
        )
      );

      this.sharedService.createNotification('success', `Role "${roleToDelete.policy_name}" deleted successfully!`);

      // Remove from roles list
      this.rolesList = this.rolesList.filter((r) => r.id !== roleToDelete.id);
      this.closeDeleteRoleModal();

      // Switch to next available role
      if (this.rolesList.length > 0) {
        await this.doSelectRole(this.rolesList[0]);
      } else {
        this.selectedRole = null;
        this.selectedRoleId = 0;
        this.rawPages = [];
        this.treePages = [];
        this.router.navigate(['/home/roles']);
      }
    } catch (err: any) {
      console.warn('[RolesManagement] deleteClientRole error:', err);
      const errMsg =
        err?.error?.message ||
        err?.error?.error ||
        err?.message ||
        'Failed to delete role.';
      this.sharedService.createNotification('error', errMsg);
      this.closeDeleteRoleModal();
    } finally {
      this.isDeletingRole = false;
      this.cdr.detectChanges();
    }
  }

  getRoleDisplayName(role: any): string {
    const rawName = (role?.policy_name || '').toString().trim();
    if (!rawName) return '';

    const lower = rawName.toLowerCase();
    const slug = lower.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    const key = `roles-page.role-names.${lower}`;
    const translated = this.translateService.instant(key);
    if (translated && translated !== key) return translated;

    const currentLang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    if (currentLang === 'ar') {
      const map: Record<string, string> = {
        'sub_user': 'مستخدم فرعي',
        'subuser': 'مستخدم فرعي',
        'sub user': 'مستخدم فرعي',
        'sub-user': 'مستخدم فرعي',
        'roles management': 'إدارة الأدوار',
        'roles-management': 'إدارة الأدوار',
        'directory': 'الدليل',
        'admin': 'مدير',
        'administrator': 'المسؤول العام',
        'manager': 'مدير',
        'campaign manager': 'مدير الحملات',
        'developer': 'مطور',
        'developer & technical': 'مطور وتقني',
        'editor': 'محرر',
        'viewer': 'مشاهد',
        'auditor': 'مدقق',
      };
      if (map[lower]) return map[lower];
      if (map[slug]) return map[slug];
    }
    return rawName;
  }

  getRoleDisplayDescription(role: any): string {
    const rawDesc = (role?.description || '').toString().trim();
    if (!rawDesc) {
      return this.translateService.instant('roles-page.default-role-desc');
    }

    const currentLang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    if (currentLang === 'ar') {
      const lower = rawDesc.toLowerCase();
      if (lower === 'sub_user' || lower === 'subuser' || lower === 'sub user') return 'مستخدم فرعي مخصص للمنصة';
      if (lower === 'description role' || lower === 'description roles') return 'دور مخصص لمنح الصلاحيات للمستخدمين';
      if (lower.startsWith('explicabo')) return 'إدارة صلاحيات وأذونات الوصول لمختلف أقسام المنصة';
      if (
        lower === 'custom organizational policy defining specific client portal page permissions.' ||
        lower === 'custom role page visibility policy.'
      ) {
        return this.translateService.instant('roles-page.default-role-desc');
      }
      if (lower.includes('unrestricted master access across all platform modules')) {
        return this.translateService.instant('roles-page.preset-admin-desc');
      }
      if (lower.includes('create and manage interactive contests')) {
        return this.translateService.instant('roles-page.preset-manager-desc');
      }
      if (lower.includes('api keys, sdk documentation')) {
        return this.translateService.instant('roles-page.preset-dev-desc');
      }
    }
    return rawDesc;
  }

  /** Look up a translation, returning null when the key is absent. */
  private lookup(path: string): string | null {
    const value = this.translateService.instant(path);
    // ngx-translate echoes the path back when the key is missing.
    return value && value !== path ? value : null;
  }

  /**
   * Localized page label. Keyed on page_key (stable) rather than the display
   * name, and shared with the Pages screen via client-pages.page-names so the
   * two tables and the sidebar can never disagree. Pages the client added
   * themselves fall back to the name stored on the server.
   */
  getPageDisplayName(page: any): string {
    const key = (page?.page_key || '').toString().trim().toLowerCase();
    if (key) {
      const translated = this.lookup(`client-pages.page-names.${key}`);
      if (translated) return translated;
    }
    return (page?.page_name || '').toString().trim();
  }

  /** Localized page key label; the real page_key stays in the title tooltip. */
  getPageKeyLabel(page: any): string {
    const raw = (page?.page_key || '').toString().trim();
    if (!raw) return '';
    return this.lookup(`client-pages.page-names.${raw.toLowerCase()}`) ?? raw;
  }

  /** Localized route label, translated one segment at a time. */
  getPageUrlLabel(page: any): string {
    const url = (page?.url || '').toString().trim();
    if (!url) return '';
    const parts = url.split('/').filter(Boolean);
    if (parts.length === 0) return url;
    return parts
      .map((seg: string) => this.lookup(`client-pages.url-segments.${seg.toLowerCase()}`) ?? seg)
      .join(' / ');
  }

  /** Localized label for a permission action column (View / Create / Edit / Delete). */
  getActionLabel(action: { key: string; label: string }): string {
    return this.lookup(`roles-page.action-${action.key}`) ?? action.label;
  }
}
